// UserActionsModal.jsx
import React, { useEffect, useRef, useState } from "react";

import { USER_ACTIONS_NAV } from "routes/APP_ROUTES";
import UserBalance from "./UserBalance";
import { useAuth } from "provider/AuthContext";
import Modal from "components/ui/Modal";
import { cn } from "helper/Utils";

import { MdClose } from "react-icons/md";
import UserSettingsMenu from "./UserSettingsMenu";

export const UserActionsModal = ({ isOpen, onClose, selectedKey, handleUserSettingMenuItemClick }) => {
  const buttonRefs = useRef({});
  const { user } = useAuth();

  const [disclosureOpened, setDisclosureOpened] = useState(null);
  const [selectedTab, setSelectedTab] = useState(null);
  const [isMobile, setIsMobile] = useState(window?.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const userActionNavItems = USER_ACTIONS_NAV();

  useEffect(() => {
    if (selectedKey) {
      const keyToCompare = typeof selectedKey === "object" ? selectedKey.key : selectedKey;
      const tab = userActionNavItems
        .flatMap((item) => item.child || item)
        .find((child) => child.key === keyToCompare);
      setSelectedTab(tab || null);
    }
  }, [selectedKey, isMobile]);

  useEffect(() => {
    const activeButton = buttonRefs.current[selectedKey?.key];
    if (activeButton) {
      activeButton.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
        inline: "center",
      });
    }
  }, [selectedKey]);

  const handleBackButton = () => {
    setDisclosureOpened(null);
    handleUserSettingMenuItemClick(null);
  };

  return (
    <>
      {isOpen ? (
        <Modal
          className="md:max-w-[1340px] md:h-auto h-screen bg-body md:top-auto top[49px] w-full md:mt-0 mt-auto"
          isOpen={isOpen}
          onClose={onClose}
          closeBtn={false}
          backdrop={isMobile ? false : true}
        >
          {/* Parent is relative; no own scrolling (modal provides the only scroll) */}
          <div className="flex relative min-h-0 w-full">
            {/* LEFT: remove inner scroll/height locking */}
            <div className="md:max-w-80 w-full flex flex-col px-3 pt-4 pb-7 md:bg-white md:bg-opacity-10">
              <UserBalance user={user} handleUserSettingMenuItemClick={handleUserSettingMenuItemClick} />

              <UserSettingsMenu
                handleUserSettingMenuItemClick={handleUserSettingMenuItemClick}
                isDisclosureOpened={disclosureOpened}
                setDisclosureOpened={setDisclosureOpened}
                handleBackButton={handleBackButton}
                onClose={onClose}
                selectedKey={selectedKey}
              />
            </div>

            {/* RIGHT: slide-in panel, no own vertical scroll */}
            <div
              className={cn(
                "w-full md:relative absolute inset-0 left-0 bg-body z-100 transition-all duration-300",
                "md:overflow-hidden overflow-visible", // no vertical scroll here
                selectedKey ? "translate-x-0" : "translate-x-full"
              )}
            >
              <div className="md:sticky-top px-3 h-12 md:flex hidden justify-between items-center bg-white bg-opacity-10 border-b border-white/10">
                <label className="text-[14px]">{selectedTab?.label}</label>
                <button
                  onClick={onClose}
                  className="text-slate-400 leading-none rounded-lg right-4 outline-none focus:outline-none absolute z-20 w-9 h-9 flex justify-center items-center"
                >
                  <MdClose className="text-gray-400 w-[24px] hover:text-[#fff]" size={30} />
                </button>
              </div>

              <div
                className={cn(
                  "md:hidden items-center gap-2 sm:p-3 p-2 sm:text-[16px] text-sm leading-none",
                  selectedKey ? "flex" : "hidden"
                )}
              >
                <i
                  className={cn(
                    "bc-i-small-arrow-left flex justify-center items-center h-5 w-5 text-[10px] leading-none rounded-full bg-white bg-opacity-60 text-body"
                  )}
                  onClick={handleBackButton}
                />{" "}
                {selectedTab?.title ? selectedTab?.title : selectedTab?.label}
              </div>

              {selectedTab && selectedKey ? (
                <>
                  <nav className="md:hidden flex justify-start space-x-2 overflow-x-auto hide-scrollbar border-b border-b-[rgb(45,62,97,0.3)]">
                    {userActionNavItems
                      .find((fi) => fi.key === selectedKey.parentKey)
                      ?.child.map((item) => {
                        const isActive = item?.key === selectedKey?.key;
                        return (
                          <button
                            type="button"
                            key={item.key}
                            ref={(el) => (buttonRefs.current[item.key] = el)}
                            onClick={() => handleUserSettingMenuItemClick(item)}
                            className={cn(
                              "tracking-wider uppercase flex flex-auto justify-center items-center text-[9px] font-semibold text-nowrap text-white/70 border-b-2 border-transparent",
                              "py-2 px-2",
                              isActive && "text-white/100 border-b-[#e3910a]"
                            )}
                          >
                            {item.label}
                          </button>
                        );
                      })}
                  </nav>

                  {/* Content: no vertical overflow here; outer modal scrolls */}
                  <div className="bg-body md:p-3.5  md:py-3 pt-3 md:min-h-[calc(100vh-240px)] min-h-[calc(100vh-150px)]">
                    {selectedTab.component}
                  </div>
                </>
              ) : (
                <p className="px-3 py-4">No component available for the selected key.</p>
              )}
            </div>
          </div>
        </Modal>
      ) : null}
    </>
  );
};
